/**
 * Malcriados · catalogo.
 *
 * El servidor pinta todos los productos con su precio por numero de retratos
 * en atributos data-precio-N (ver snippets/mc-tarjeta-producto.liquid). Aqui solo
 * se cambia lo visible:
 *
 * - Filtro "N° de mascotas": muestra el precio de esa variante y oculta los
 *   productos que no la tienen. Queda en la URL como ?mascotas=N.
 * - Pestañas (pagina con todos los grupos): muestran un grupo o todos. Queda en
 *   la URL como ?coleccion=handle.
 *
 * Sin JS se ve todo, sin filtro, y las pestañas saltan a cada grupo.
 */

const PARAM_MASCOTAS = 'mascotas';
const PARAM_COLECCION = 'coleccion';

/** @returns {{ n: number, coleccion: string }} */
function leerEstado() {
  const parametros = new URL(window.location.href).searchParams;
  const n = Number.parseInt(parametros.get(PARAM_MASCOTAS) || '', 10);
  return {
    n: Number.isInteger(n) && n > 0 ? n : 0,
    coleccion: parametros.get(PARAM_COLECCION) || 'todos',
  };
}

/**
 * @param {number} n
 * @param {string} coleccion
 */
function guardarEstado(n, coleccion) {
  const url = new URL(window.location.href);
  if (n > 0) url.searchParams.set(PARAM_MASCOTAS, String(n));
  else url.searchParams.delete(PARAM_MASCOTAS);
  if (coleccion && coleccion !== 'todos') url.searchParams.set(PARAM_COLECCION, coleccion);
  else url.searchParams.delete(PARAM_COLECCION);
  url.hash = '';
  window.history.replaceState(window.history.state, '', url);
}

/**
 * La foto de la tarjeta sigue al filtro: mc-tarjeta.js la cambia a la de esa cantidad de retratos.
 * @param {HTMLElement} tarjeta
 * @param {number} n  0 = sin filtro
 */
function avisarRetratos(tarjeta, n) {
  const actual = tarjeta.dataset.mcN ?? '';
  const nuevo = n ? String(n) : '';
  if (actual === nuevo) return;
  if (nuevo) tarjeta.dataset.mcN = nuevo;
  else delete tarjeta.dataset.mcN;
  tarjeta.dispatchEvent(new CustomEvent('mc:retratos', { bubbles: true }));
}

/**
 * @param {HTMLElement} tarjeta
 * @param {number} n
 * @param {HTMLElement} raiz
 * @returns {boolean} true si la tarjeta queda visible
 */
function pintarTarjeta(tarjeta, n, raiz) {
  const precio = tarjeta.querySelector('[data-mc-precio]');
  const detalle = tarjeta.querySelector('[data-mc-detalle]');
  const desde = tarjeta.querySelector('[data-mc-desde]');

  if (!n) {
    tarjeta.hidden = false;
    avisarRetratos(tarjeta, 0);
    if (precio instanceof HTMLElement) precio.textContent = precio.dataset.base || '';
    if (detalle instanceof HTMLElement) detalle.textContent = detalle.dataset.base || '';
    if (desde instanceof HTMLElement) desde.hidden = false;
    return true;
  }

  const precioN = tarjeta.getAttribute(`data-precio-${n}`);
  if (precioN === null) {
    tarjeta.hidden = true;
    return false;
  }

  tarjeta.hidden = false;
  avisarRetratos(tarjeta, n);
  if (precio) precio.textContent = precioN;
  if (detalle) {
    const plantilla = n === 1 ? raiz.dataset.textoUno : raiz.dataset.textoVarios;
    detalle.textContent = (plantilla || '').replaceAll('[n]', String(n));
  }
  if (desde instanceof HTMLElement) desde.hidden = true;
  return true;
}

/**
 * Los enlaces que salen de la pagina (otras colecciones, paginas) llevan el filtro elegido.
 * @param {HTMLElement} raiz
 * @param {number} n
 */
function conservarFiltro(raiz, n) {
  for (const enlace of raiz.querySelectorAll('a[data-mc-conserva]')) {
    if (!(enlace instanceof HTMLAnchorElement)) continue;
    const url = new URL(enlace.href, window.location.href);
    if (n > 0) url.searchParams.set(PARAM_MASCOTAS, String(n));
    else url.searchParams.delete(PARAM_MASCOTAS);
    enlace.href = url.pathname + url.search + url.hash;
  }
}

/** @param {HTMLElement} raiz */
function aplicar(raiz) {
  const estado = leerEstado();
  const maximo = Number.parseInt(raiz.dataset.max || '5', 10) || 5;
  const n = estado.n <= maximo ? estado.n : 0;
  const porGrupos = raiz.dataset.modo === 'grupos';

  const grupos = /** @type {HTMLElement[]} */ ([...raiz.querySelectorAll('[data-mc-grupo]')]);
  let coleccion = 'todos';
  if (porGrupos && grupos.some((grupo) => grupo.dataset.mcGrupo === estado.coleccion)) {
    coleccion = estado.coleccion;
  }

  for (const boton of raiz.querySelectorAll('[data-mc-mascotas]')) {
    boton.setAttribute('aria-pressed', String(Number(boton.getAttribute('data-mc-mascotas')) === n));
  }

  if (porGrupos) {
    for (const pestana of raiz.querySelectorAll('[data-mc-pestana]')) {
      if (pestana.getAttribute('data-mc-pestana') === coleccion) pestana.setAttribute('aria-current', 'true');
      else pestana.removeAttribute('aria-current');
    }
  }

  let total = 0;
  for (const grupo of grupos) {
    let visibles = 0;
    for (const tarjeta of grupo.querySelectorAll('[data-mc-tarjeta]')) {
      if (tarjeta instanceof HTMLElement && pintarTarjeta(tarjeta, n, raiz)) visibles += 1;
    }
    const elegido = coleccion === 'todos' || grupo.dataset.mcGrupo === coleccion;
    grupo.hidden = !elegido || visibles === 0;
    if (!grupo.hidden) total += visibles;
  }

  const vacio = raiz.querySelector('[data-mc-vacio]');
  if (vacio instanceof HTMLElement) vacio.hidden = total > 0 || grupos.length === 0;

  const conteo = raiz.querySelector('[data-mc-conteo]');
  if (conteo) {
    let texto = `${total} ${total === 1 ? 'producto' : 'productos'}`;
    if (n) texto += ` para ${n} ${n === 1 ? 'mascota' : 'mascotas'}, con su precio`;
    conteo.textContent = n || coleccion !== 'todos' ? texto : '';
  }

  conservarFiltro(raiz, n);
}

function aplicarTodo() {
  for (const raiz of document.querySelectorAll('[data-mc-catalogo]')) {
    if (raiz instanceof HTMLElement) aplicar(raiz);
  }
}

/**
 * Si la barra ya esta fija arriba, vuelve al inicio de la lista para que el
 * cambio se vea. Funciona igual si se desplaza la ventana o .page-wrapper.
 * @param {HTMLElement} raiz
 */
function volverALaLista(raiz) {
  const barra = raiz.querySelector('[data-mc-barra]');
  const lista = raiz.querySelector('.mc-catalogo__lista');
  if (!barra || !lista) return;
  if (lista.getBoundingClientRect().top >= barra.getBoundingClientRect().bottom) return;
  const sinMovimiento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  lista.scrollIntoView({ block: 'start', behavior: sinMovimiento ? 'auto' : 'smooth' });
}

document.addEventListener('click', (evento) => {
  const origen = evento.target instanceof Element ? evento.target : null;
  const raiz = origen?.closest('[data-mc-catalogo]');
  if (!origen || !(raiz instanceof HTMLElement)) return;

  const estado = leerEstado();

  const boton = origen.closest('[data-mc-mascotas]');
  if (boton) {
    const elegido = Number(boton.getAttribute('data-mc-mascotas'));
    guardarEstado(estado.n === elegido ? 0 : elegido, estado.coleccion);
    aplicar(raiz);
    volverALaLista(raiz);
    return;
  }

  const pestana = origen.closest('[data-mc-pestana]');
  if (pestana && raiz.dataset.modo === 'grupos') {
    evento.preventDefault();
    guardarEstado(estado.n, pestana.getAttribute('data-mc-pestana') || 'todos');
    aplicar(raiz);
    volverALaLista(raiz);
    return;
  }

  if (origen.closest('[data-mc-reiniciar]')) {
    guardarEstado(0, 'todos');
    aplicar(raiz);
    volverALaLista(raiz);
  }
});

window.addEventListener('popstate', aplicarTodo);
/* El editor de temas vuelve a pintar la seccion: se aplica de nuevo el estado. */
document.addEventListener('shopify:section:load', aplicarTodo);

aplicarTodo();
